// Shared Data Service for Cross-Platform Compatibility
// This service manages data that needs to be shared between mobile and web apps

export interface SharedAppointment {
  id: string;
  customerName: string;
  phone: string;
  email?: string;
  serviceType: string;
  appointmentDate: string;
  appointmentTime: string;
  estimatedCost: number;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled';
  notes?: string;
  businessOwnerId: string;
  createdAt: string;
  updatedAt: string;
}

export interface SharedUser {
  id: string;
  name: string;
  email: string;
  phone?: string;
  userType: 'customer' | 'business_owner';
  businessOwnerId?: string;
  createdAt: string;
}

export interface SharedBusinessData {
  id: string;
  name: string;
  ownerId: string;
  services: Array<{
    id: number;
    name: string;
    price: number;
    duration: number;
  }>;
  workers: Array<{
    id: string;
    name: string;
    role: string;
    status: 'active' | 'inactive';
  }>;
  createdAt: string;
}

class SharedDataService {
  private static instance: SharedDataService;
  private data: {
    appointments: SharedAppointment[];
    users: SharedUser[];
    businessData: SharedBusinessData[];
  } = {
    appointments: [],
    users: [],
    businessData: []
  };

  private constructor() {
    this.initializeDefaultData();
  }

  public static getInstance(): SharedDataService {
    if (!SharedDataService.instance) {
      SharedDataService.instance = new SharedDataService();
    }
    return SharedDataService.instance;
  }

  private initializeDefaultData() {
    // Initialize with empty data - no hardcoded test data for new users
    // All data should come from real user registrations and bookings
    this.data.users = [];
    this.data.businessData = [];
    this.data.appointments = [];
  }

  // Appointments
  public getAppointments(businessOwnerId?: string): SharedAppointment[] {
    if (businessOwnerId) {
      return this.data.appointments.filter(apt => apt.businessOwnerId === businessOwnerId);
    }
    return this.data.appointments;
  }

  public createAppointment(appointment: Omit<SharedAppointment, 'id' | 'createdAt' | 'updatedAt'>): SharedAppointment {
    const newAppointment: SharedAppointment = {
      ...appointment,
      id: `apt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    this.data.appointments.push(newAppointment);
    this.saveToStorage();
    return newAppointment;
  }

  public updateAppointment(id: string, updates: Partial<SharedAppointment>): SharedAppointment | null {
    const index = this.data.appointments.findIndex(apt => apt.id === id);
    if (index !== -1) {
      this.data.appointments[index] = {
        ...this.data.appointments[index],
        ...updates,
        updatedAt: new Date().toISOString()
      };
      this.saveToStorage();
      return this.data.appointments[index];
    }
    return null;
  }

  public deleteAppointment(id: string): boolean {
    const index = this.data.appointments.findIndex(apt => apt.id === id);
    if (index !== -1) {
      this.data.appointments.splice(index, 1);
      this.saveToStorage();
      return true;
    }
    return false;
  }

  // Users
  public getUsers(): SharedUser[] {
    return this.data.users;
  }

  public createUser(user: Omit<SharedUser, 'id' | 'createdAt'>): SharedUser {
    const newUser: SharedUser = {
      ...user,
      id: `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString()
    };
    
    this.data.users.push(newUser);
    this.saveToStorage();
    return newUser;
  }

  public getUserById(id: string): SharedUser | null {
    return this.data.users.find(user => user.id === id) || null;
  }

  public getUserByEmail(email: string): SharedUser | null {
    return this.data.users.find(user => user.email === email) || null;
  }

  // Business Data
  public getBusinessData(businessOwnerId: string): SharedBusinessData | null {
    return this.data.businessData.find(business => business.ownerId === businessOwnerId) || null;
  }

  public getServices(businessOwnerId: string) {
    const business = this.getBusinessData(businessOwnerId);
    return business?.services || [];
  }

  public getWorkers(businessOwnerId: string) {
    const business = this.getBusinessData(businessOwnerId);
    return business?.workers || [];
  }

  // Storage methods
  private saveToStorage() {
    try {
      // For web, use localStorage
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem('sharedData', JSON.stringify(this.data));
      }
      // For React Native, we'll use AsyncStorage
      // This will be handled by the platform-specific implementations
    } catch (error) {
      console.error('Error saving shared data:', error);
    }
  }

  private loadFromStorage() {
    try {
      // For web, use localStorage
      if (typeof window !== 'undefined' && window.localStorage) {
        const stored = localStorage.getItem('sharedData');
        if (stored) {
          this.data = JSON.parse(stored);
        }
      }
      // For React Native, we'll use AsyncStorage
      // This will be handled by the platform-specific implementations
    } catch (error) {
      console.error('Error loading shared data:', error);
    }
  }

  // Cross-platform sync method
  public syncData() {
    this.loadFromStorage();
  }

  // Export data for backup/transfer
  public exportData() {
    return JSON.stringify(this.data, null, 2);
  }

  // Import data from backup/transfer
  public importData(dataString: string) {
    try {
      const importedData = JSON.parse(dataString);
      this.data = importedData;
      this.saveToStorage();
      return true;
    } catch (error) {
      console.error('Error importing data:', error);
      return false;
    }
  }
}

export default SharedDataService;




